"use client";

// Subir o cambiar la foto de perfil.
//
// La foto se achica y se recorta cuadrada EN EL NAVEGADOR antes de subirla.
// Una foto de celular son 4 MB: en 3G eso es medio minuto de silencio en el que
// el técnico cree que falló y la manda tres veces. Recortada a 512 px queda en
// unos 60 KB y sube al instante. Además el recorte cuadrado acá evita que la
// cara quede cortada al mostrarse en un círculo.
import { useRef, useState } from "react";
import Avatar from "./Avatar";
import Icon from "./Icon";
import { ApiError, api } from "../lib/api";

const LADO = 512;
const CALIDAD = 0.85;

async function aCuadradoChico(archivo: File): Promise<Blob> {
  const bitmap = await createImageBitmap(archivo);
  const lado = Math.min(bitmap.width, bitmap.height, LADO * 2);
  const canvas = document.createElement("canvas");
  canvas.width = LADO;
  canvas.height = LADO;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No pudimos procesar la foto en este navegador.");
  // Recorte centrado: es donde está la cara en el 95% de las fotos de perfil
  const sx = (bitmap.width - lado) / 2;
  const sy = (bitmap.height - lado) / 2;
  ctx.drawImage(bitmap, sx, sy, lado, lado, 0, 0, LADO, LADO);
  bitmap.close();
  return new Promise((res, rej) =>
    canvas.toBlob(
      (b) => (b ? res(b) : rej(new Error("No pudimos procesar la foto."))),
      "image/jpeg",
      CALIDAD,
    ),
  );
}

export default function SubirFoto({
  userId,
  nombre,
  onCambio,
}: {
  userId: string;
  nombre: string;
  onCambio?: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState("");
  const [borrada, setBorrada] = useState(false);

  async function elegida(archivos: FileList | null) {
    const f = archivos?.[0];
    if (!f) return;
    setError("");
    if (!f.type.startsWith("image/")) {
      setError("Elegí una foto, no otro tipo de archivo.");
      return;
    }
    setSubiendo(true);
    try {
      const chica = await aCuadradoChico(f);
      const fd = new FormData();
      fd.append("foto", chica, "perfil.jpg");
      const token = localStorage.getItem("busco.access");
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL ?? "/api"}/users/me/avatar`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      const cuerpo = await res.json().catch(() => null);
      if (!res.ok) {
        const m = cuerpo?.message;
        throw new Error(Array.isArray(m) ? m.join(". ") : m || "No pudimos subir la foto.");
      }
      setVersion(Date.now());
      setBorrada(false);
      onCambio?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No pudimos subir la foto.");
    } finally {
      setSubiendo(false);
      if (input.current) input.current.value = "";
    }
  }

  async function borrar() {
    setError("");
    setSubiendo(true);
    try {
      await api("/users/me/avatar", { method: "DELETE" });
      setBorrada(true);
      setVersion(Date.now());
      onCambio?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No pudimos quitar la foto.");
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <div className="rounded-xl bg-[#1A2123] border border-white/10 p-4">
      <div className="flex items-center gap-4">
        <Avatar
          userId={borrada ? null : userId}
          nombre={nombre}
          size={72}
          version={version}
        />
        <div className="min-w-0 flex-1">
          <strong className="text-[14.5px]">Tu foto de perfil</strong>
          <p className="text-[12.5px] text-[#808D88] leading-relaxed mt-0.5">
            La ven los clientes al elegir a quién le dejan su equipo. Una cara da
            más confianza que cualquier descripción.
          </p>
          {error && <p className="text-[12.5px] text-[#DE705C] mt-1.5">{error}</p>}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          onClick={() => input.current?.click()}
          disabled={subiendo}
          className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg bg-[#E8A23C] text-[#201404] text-[13.5px] font-bold hover:brightness-105 disabled:opacity-50"
        >
          <Icon name="camera" size={16} />
          {subiendo ? "Subiendo…" : "Elegir foto"}
        </button>
        {!borrada && (
          <button
            onClick={borrar}
            disabled={subiendo}
            className="min-h-[44px] px-4 rounded-lg border border-white/15 text-[13.5px] text-[#808D88] hover:text-[#DE705C] hover:bg-white/5 disabled:opacity-50"
          >
            Quitar
          </button>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept="image/*"
        onChange={(e) => void elegida(e.target.files)}
        className="hidden"
      />
    </div>
  );
}
